import React, {useState, useContext, useEffect, useRef} from 'react';
import {useQueryClient} from "react-query";
import __ from "lodash";
import moment from "moment";
import {FormControl, FormGroup, Grid, useTheme} from "@material-ui/core";
import {
  DatePicker,
  FormDialog, LabelledPaper,
  MaskedNumber,
  MaskedText, MaskedTime
} from '../../../controls';
import AddEnquiryValidator from "../validators/AddEnquiryValidator";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import ActionsBar from '../../../controls/ActionsBar';
import {EmailViewer, nextActionDateTime} from "./EnquiryHelperFunctions";
import EnquiryCargoEditor from './EnquiryCargoEditor';
import {useEmailMessages} from "../../../services/emailMessages";
import CommodityCheckDialog from "../../Settings/components/CommodityCheckDialog";
import NewCompanyCaptureDialog from "../../Companies/components/NewCompanyCaptureDialog";
import CompanyContactLookup from "../../Companies/components/CompanyContactLookup";

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}

const AddEnquiryDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const {classes, extraKeys, formatChars, defaults} = props;

  const SystemData = props.systemData;
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const formMode = props.formMode || "WEB";

  const operatorName = __.startCase(__.toLower(process.env.REACT_APP_COMPANY)) || "Magma";

  const [dialogStates, setDialogStates] = useState({...props.dialogStates});
  const [editing, setEditing] = useState(true);
  const [requestSent, setRequestSent] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  const companyRef = useRef();

  const close = () => {
    setDialogStates({...dialogStates, showDialog:false, record: {}, title: 'Add Enquiry'});
  }

  useEffect(()=> {
    const open = async (record, updateKeys) => {
      const title = "New Enquiry";
      // record['sourceEmailUuid'] = '003d15bc-067b-4d2f-bdb9-a31ed3d82db0';
      const followUpTime = nextActionDateTime();
      record["respondBy"] = followUpTime;
      record["nextActivity"] = "REVIEW";
      setDialogStates(
        {
          title,
          ...dialogStates,
          showDialog:true,
          extraKeys: updateKeys||extraKeys,
          record,
        }
      );
    }
    eventBus.bind("AddEnquiryDialog.open", open);

    return () => {
      eventBus.unbind("AddEnquiryDialog.open", open);
    }
  }, [dialogStates, eventBus, extraKeys]);

  const queryClient = useQueryClient();
  const emailRecord = useEmailMessages({queryClient, extraKeys:[dialogStates.record?.sourceEmailUuid], useCache: true});

  const getButtons = () => {
    if (formMode === "OPERATOR") {
      return (
        {
          save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{return status.error}},
          cancel:{icon: "cancel", key: "Cancel", label: "Close", color: "primary"},
        }
      );
    }
    return (
      {
        save:{icon: "send", key: "Save", label: "Send", color: "primary", disabled: (status)=>{return status.error}},
        cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
      }
    );
  }

  const handleCargoChanged = (changes, props) => {
    const {recordChange} = props;
    if (changes && changes.record) {
      recordChange({...changes.record});
      if (changes.resolve) {
        changes.resolve();
      }
    }
  }

  if (dialogStates.showDialog) {
    return (
      <div key="formContainer" style={{width: "100%", textAlign:"left" }}>
        <FormDialog
          key="addEnquiry"
          title={dialogStates.title}
          message={dialogStates.dialogMessage}
          open={dialogStates.showDialog}
          dialogOptions={{maxWidth: false, PaperProps: paperProps }}
          record={dialogStates.record}
          validator={AddEnquiryValidator}
          validatorPosition="top"
          alert={alert}
          formHeightOffset={320}
          titleBarStyle={{backgroundColor: '#3f3f3f', color: theme.palette.primary.main}}
          buttons={getButtons()}
          onChange={(state) => {
            if (state.buttonPressed === "cancel") {
              close();
              if (props.close)
                props.close();
            }
            if (state.buttonPressed === "save") {
              let replySubject = null;
              if (dialogStates.record?.sourceEmailUuid && emailRecord)
                replySubject = `RE: ${emailRecord.subject}`

              SystemData.toggleLoadingProgress(true);
              props.handleAdd.mutate({
                extraKeys,
                record: {...state.record, nextActionDate: state.record.respondBy, replySubject: replySubject},
                postSuccess: () => {
                  setRequestSent(true);
                  SystemData.toggleLoadingProgress(false);
                  queryClient.invalidateQueries(["Enquiries"]);
                  close();
                  if (props.close)
                    props.close();
                },
                postError: (data) => {
                  SystemData.toggleLoadingProgress(false);
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred adding the Enquiry`);
                  }
                }
              });

            } else {
              console.log("Unhandled FormDialog onChange");
            }
          }}
        >
          <div style={{display: 'inline-block', width: dialogStates.record?.sourceEmailUuid?1100:1100}}>
            <Grid container checkChildren={true} spacing={3}>
              {dialogStates.dialogMessage
                ?
                  <Grid item xs={12} />
                :
                  <></>
              }
              <Grid item xs={12} checkChildren={true} row>
                <SectionHeading>Company and Contact Information</SectionHeading>
                <FormControl checkChildren={true}
                  key="FC-company"
                  style={{
                    display: "block",
                    backgroundColor: "#fdfdfd",
                    boxShadow: "none",
                    border: "2px solid #aaaaaa",
                    borderRadius: 0,
                  }}
                >
                  <FormGroup checkChildren={true} key="FG-company" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={4} checkChildren={true}>
                        <CompanyUuidLookup
                          companyType={"CUSTOMER"}
                          id="companyName"
                          label="Company"
                          shrink={true}
                          readOnly={editing!==true}
                          returnType={"object"}
                          includeNew={true}
                          allowEmpty={true}
                          onChange={(returnObject, props) => {
                            debugger;
                            if (returnObject.found) {
                              const company = returnObject.data
                              if (company.companyCode === "NEW FORWARDER" && eventBus) {
                                eventBus.trigger('NewCompanyCapture.open');
                                document.getElementById("contactName").focus();
                              }
                              let data = {
                                ...props.record,
                              }
                              if (company.companyUuid && company.companyUuid!== props.record.companyUuid) {
                                data = {
                                  ...props.record,
                                  companyName: company.companyName,
                                  companyUuid: company.companyUuid,
                                  contactUuid: null,
                                  contactSalutation: "",
                                  contactFirstName: "",
                                  contactLastName: "",
                                  contactPhone: "",
                                  contactEmail: "",
                                  contactName: "",
                                }
                                props.recordChange(data);
                              } else if (!company.companyUuid) {
                                props.onChange(returnObject.value);
                              } else {
                                data = {
                                  ...props.record,
                                  companyName: company.companyName,
                                  companyUuid: company.companyUuid,
                                }
                                props.recordChange(data);
                              }
                            } else {
                              props.onChange(returnObject.value);
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        {(record, changeRecord)=>
                          <CompanyContactLookup
                            id="contactName"
                            label="Submitted By"
                            companyUuid={record.companyUuid}
                            // contactType="Sales Agent"
                            returnType="object"
                            allowFreeInput={true}
                            freeSolo={false}
                            readOnly={false}
                            inputLabelProps={{shrink: true}}
                            shrink={true}
                            fullWidth
                            mask={"x".repeat(50)}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={formatChars}
                            onChange={(contactDetails, props) => {
                              if (contactDetails.found) {
                                const data = {
                                  ...props.record,
                                  contactUuid: contactDetails.data.companyContactUuid,
                                  contactSalutation: contactDetails.data.title,
                                  contactFirstName: contactDetails.data.forename,
                                  contactLastName: contactDetails.data.surname,
                                  contactPhone: contactDetails.data.phone,
                                  contactEmail: contactDetails.data.email,
                                  contactName: `${contactDetails.data.forename || ""} ${contactDetails.data.surname || ""}`,
                                }
                                props.recordChange(data);
                              } else {
                                if (contactDetails.value === "") {
                                  props.onChange(contactDetails.value);
                                } else {
                                  const data = {
                                    ...props.record,
                                    contactUuid: null,
                                    contactSalutation: "",
                                    contactFirstName: "",
                                    contactLastName: "",
                                    contactPhone: "",
                                    contactEmail: "",
                                    contactName: contactDetails.value,
                                  }
                                  props.recordChange(data);
                                }
                              }
                            }}
                          />
                        }
                      </Grid>
                      {/*<Grid item xs={3} checkChildren={true}>*/}
                      {/*  <MaskedText*/}
                      {/*    id="contactName"*/}
                      {/*    label="Submitted By"*/}
                      {/*    readOnly={false}*/}
                      {/*    inputLabelProps={{shrink: true}}*/}
                      {/*    fullWidth*/}
                      {/*    mask={"x".repeat(50)}*/}
                      {/*    maskChar={""}*/}
                      {/*    alwaysShowMask={false}*/}
                      {/*    formatChars={formatChars}*/}
                      {/*  />*/}
                      {/*</Grid>*/}
                      <Grid item xs={3} checkChildren={true}>
                        <MaskedText
                          id="contactEmail"
                          label="Email"
                          readOnly={editing !== true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          mask={"x".repeat(50)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                        />
                      </Grid>
                      <Grid item xs={2} checkChildren={true}>
                        <MaskedNumber
                          id="contactPhone"
                          label="Phone Number"
                          readOnly={editing !== true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          format="+##############"
                        />
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} checkChildren={true} row>
                <SectionHeading>Availability Details</SectionHeading>
                <FormControl checkChildren={true}
                  key="FC-availabilityDetails"
                  style={{
                    display: "block",
                    backgroundColor: "#fdfdfd",
                    boxShadow: "none",
                    border: "2px solid #aaaaaa",
                    borderRadius: 0,
                  }}
                >
                  <FormGroup checkChildren={true} key="FG-availabilityDetails" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={3} checkChildren={true} >
                        <DatePicker
                          id="cargoAvailability"
                          label={`Delivery at ${operatorName} Departure Point`}
                          readOnly={editing!==true}
                          fullWidth
                          inputLabelProps={{shrink: true}}
                          minDate={ undefined }
                          disableToolbar={true}
                          autoOk={true}
                          format="DD/MM/YYYY"
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true} >
                        {record=>
                          <DatePicker
                            id="requiredAtDestination"
                            label="Required at Destination by"
                            readOnly={editing!==true}
                            fullWidth
                            inputLabelProps={{shrink: true}}
                            minDate={ record.cargoAvailability ? moment.utc(record.cargoAvailability).format("YYYY-MM-DD") : undefined}
                            disableToolbar={true}
                            autoOk={true}
                            format="DD/MM/YYYY"
                          />
                        }
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} checkChildren={true}>
                <div>
                  {(record) => (
                    <EnquiryCargoEditor
                      record={record}
                      extraKeys={extraKeys}
                      canEdit={true}
                      classes={classes}
                      onChange={handleCargoChanged}
                      defaultValues={defaults}
                      weightUom={record.weightUom}
                    />
                  )}
                  {(record) => (
                    <ActionsBar
                      eventPrefix="EnquiryCargoEditor"
                      record={record}
                      state={{}}
                      formDialog={{addRecordChange: true}}
                      actions={[
                        {label: 'Add Consignment', key: 'Add Consignment', type: 'button', icon: "add_circle", color: "primary", eventName: 'EnquiryCargoEditor.addCargo'},
                        {label: 'Commodity Checker', key: 'Commodity Checker', type: 'button', icon: "rule_folder", color: "primary", eventName: 'CommodityCheck.open'}
                      ]}
                    />
                  )}
                </div>
              </Grid>

              <Grid item xs={12} checkChildren={true} row>
                <SectionHeading>Follow-up Details</SectionHeading>
                <FormControl checkChildren={true}
                             key="FC-followUp"
                             style={{
                               display: "block",
                               backgroundColor: "#fdfdfd",
                               boxShadow: "none",
                               border: "2px solid #aaaaaa",
                               borderRadius: 0,
                             }}
                >
                  <FormGroup checkChildren={true} key="FG-followUp" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={3} checkChildren={true} >
                        <DatePicker
                          id="respondBy"
                          label="Respond By Date"
                          readOnly={editing!==true}
                          fullWidth
                          inputLabelProps={{shrink: true}}
                          minDate={moment.utc().startOf('day').format("YYYY-MM-DD")}
                          disableToolbar={true}
                          autoOk={true}
                          format="DD/MM/YYYY"
                          onChange={(value, props) => {
                            if (!value) return;
                            const {record, recordChange} = props;
                            const previousValue = moment.utc(record.respondBy);
                            const newValue = moment.utc(value).startOf('day')
                              .add(previousValue.hours(), 'hour')
                              .add(previousValue.minutes(), 'minute')
                              .format("YYYY-MM-DD HH:mm");
                            recordChange({
                              respondBy: newValue
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true} row>
                        {(record, changeRecord) => (
                          <MaskedTime
                            id="respondBy"
                            label="Respond By Time"
                            source="timestamp"
                            onChange={(value)=>{
                              if (!value) return;
                              if (typeof value !== 'string' || value.indexOf(':') < 1 || value.split(':').length !== 2) return;
                              value = value.replace(/\s/g, '0');
                              const [hours,  minutes] =  value.split(':');
                              if (isNaN(hours) || hours < 0 || hours > 23) return;
                              if (isNaN(minutes) || minutes < 0 || hours > 59) return;
                              let respondBy = record.respondBy ?? record.respondBy;
                              const newRespondBy = moment.utc(respondBy).startOf('day')
                                .add(hours, 'hour')
                                .add(minutes, 'minute')
                                .format("YYYY-MM-DD HH:mm");
                              changeRecord({
                                respondBy: newRespondBy,
                              });
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </Grid>

              {record =>
                record.aiComments
                  ?
                    <Grid item xs={12} checkChildren={true} row>
                    <SectionHeading>AI Analysis Comments</SectionHeading>
                    <FormControl checkChildren={true}
                                 key="FC-aicomments"
                                 style={{
                                   display: "block",
                                   backgroundColor: "#fdfdfd",
                                   boxShadow: "none",
                                   border: "2px solid #aaaaaa",
                                   borderRadius: 0,
                                 }}
                    >
                      <FormGroup checkChildren={true} key="FG-aicomments" style={{padding: "8px 8px 8px 8px"}}>
                        <Grid container checkChildren={true} spacing={3}>
                          <Grid item xs={12} checkChildren={true} row>
                            <LabelledPaper {...defaultLabelledPaperProps} label={``} >
                              <FormControl checkChildren={true} key="FC-aicomments" style={{display: "block" }}>
                                <FormGroup checkChildren={true} key="FG-aicomments" style={{padding: "8px 8px 8px 8px"}}>
                                  {record.aiComments}
                                </FormGroup>
                              </FormControl>
                            </LabelledPaper>
                          </Grid>
                        </Grid>
                      </FormGroup>
                    </FormControl>
                  </Grid>
                  :
                    <></>
              }

            </Grid>
            {(record, changeRecord) =>
              <NewCompanyCaptureDialog
                title={"Capture New Company Details"}
                open={false}
                onChange={(newCompany) => {
                  changeRecord({
                    companyName: newCompany?.companyName,
                    companyUuid: newCompany?.companyUuid,
                  });
                  if (!newCompany?.companyUuid) {
                    setTimeout(() => {
                      document.getElementById("companyName").focus();
                    }, 100);
                  }
                }}
                checkChildren={true}
              />
            }
          </div>
          {dialogStates.record?.sourceEmailUuid?
            <div style={{display: 'inline-flex', width: "calc(100% - 1120px)", marginLeft: 20}}>
              <EmailViewer canEdit={false} classes={classes} queryClient={queryClient} sourceEmailUuid={dialogStates.record?.sourceEmailUuid} doNotDisplayTitle={true}/>
            </div>
          : null}
        </FormDialog>
        <CommodityCheckDialog
          systemData={SystemData}
          onChange={() => {
          }}
        />
      </div>
    );
  }
  return (<></>);
}

export default AddEnquiryDialog;
